/**
 * alertGate — do the Practice Ops cards show alert icons, and if not, what does the board say?
 *
 * WHY THIS EXISTS (2026-10-30, plan §4 "Access rule"). The icons on a card are Schedule View's alerts
 * (`scheduleView/fetchAlerts` → /schedule-view/alerts), nothing of Practice Ops' own. The service answers an
 * office with a 403 `{ unavailable, exeVersion, minExeVersion }` when it cannot compute them (see
 * src/utils/scheduleView/access.js `accessRefusalFrom`; the Schedule View store keeps it as `accessRefusal`).
 * Practice Ops has NO exe minimum itself — the board is kept live by the exe's Practice Pulse worker on any
 * version — so a refusal must never blank or break the board. It only means "no icons", and for the one
 * reason the office can ACT on (an old Teamio app) a single quiet line says so:
 *
 *   alertIconState(null)                                  → { showIcons: true,  line: null }
 *   alertIconState({ unavailable: 'EXE_TOO_OLD', ... })   → { showIcons: false, line: 'Alert icons need Teamio app 3.0.61+ (this office: 3.0.54)' }
 *   alertIconState({ unavailable: 'NOT_ENABLED' })        → { showIcons: false, line: null }   (nothing the office can do here)
 *   anything else                                         → { showIcons: false, line: null }
 *
 * The minimum in the sentence is the refusal's own `minExeVersion` (the service decides it), falling back to
 * MIN_EXE_FALLBACK, which is pinned to SCHEDULE_CHART_AUDIT_MIN_EXE by cypress/node-tests/practice-ops-alert-gate.test.js.
 * With no known office version the parenthetical is left out: 'Alert icons need Teamio app 3.0.61+'.
 *
 * NEVER_SHOW_ALERT_KEYS — alert keys Practice Ops must never draw, even if a payload carries them. The classic
 * board's own fourth icon was dropped when the cards moved to Schedule View's catalog; it is not in that
 * catalog (ALERT_KEYS, src/utils/scheduleView/settingsForm.js), so this list exists to keep it that way.
 * practice-ops-alert-order.test.js pins that no OTHER file under src/utils/practiceOps/ names it.
 *
 * ⚠️ Keep this file import-free. The node-tests execute this exact file.
 */

export const MIN_EXE_FALLBACK = '3.0.61';

export const NEVER_SHOW_ALERT_KEYS = Object.freeze(['ortho']);

function text(value) {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

/**
 * @param {?{unavailable?: string, exeVersion?: ?string, minExeVersion?: ?string}} refusal
 *   the Schedule View store's `accessRefusal`, or null while the office has access
 * @returns {{showIcons: boolean, line: ?string}}
 */
export function alertIconState(refusal) {
  if (!refusal) return { showIcons: true, line: null };
  if (typeof refusal === 'object' && refusal.unavailable === 'EXE_TOO_OLD') {
    const min = text(refusal.minExeVersion) || MIN_EXE_FALLBACK;
    const have = text(refusal.exeVersion);
    const line = `Alert icons need Teamio app ${min}+${have ? ` (this office: ${have})` : ''}`;
    return { showIcons: false, line };
  }
  return { showIcons: false, line: null };
}
